import { useState, useEffect, useRef } from 'react';
import { categories } from '@/mocks/categories';
import { Link, useLocation } from 'react-router-dom';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
  }, [location]);

  const handleCategorySelect = (url: string) => {
    setDropdownOpen(false);
    setMobileMenuOpen(false);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const isTransparent = !scrolled && isHome;

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isTransparent
          ? 'bg-gradient-to-b from-black/30 to-transparent'
          : 'bg-background-50 shadow-sm border-b border-background-200/60'
      }`}
    >
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 flex items-center justify-between h-16">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 no-underline cursor-pointer">
          <span className={`font-heading text-xl font-bold tracking-wider whitespace-nowrap ${
            isTransparent ? 'text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.5)]' : 'text-foreground-900'
          }`}>
            PANDABUY <span className="font-light italic">Sheet</span>
          </span>
        </Link>

        {/* Desktop Nav Links */}
        <div className="hidden md:flex items-center gap-6">
          <Link
            to="/"
            className={`text-sm font-medium whitespace-nowrap cursor-pointer ${
              isTransparent ? 'text-white/90 hover:text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)]' : 'text-foreground-700 hover:text-foreground-950'
            }`}
          >
            Home
          </Link>
          {isHome ? (
            <>
              <a href="#categories" className={`text-sm font-medium whitespace-nowrap cursor-pointer ${
                isTransparent ? 'text-white/90 hover:text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)]' : 'text-foreground-700 hover:text-foreground-950'
              }`}>
                Categories
              </a>
              <a href="#how-it-works" className={`text-sm font-medium whitespace-nowrap cursor-pointer ${
                isTransparent ? 'text-white/90 hover:text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)]' : 'text-foreground-700 hover:text-foreground-950'
              }`}>
                How It Works
              </a>
              <a href="#faq" className={`text-sm font-medium whitespace-nowrap cursor-pointer ${
                isTransparent ? 'text-white/90 hover:text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)]' : 'text-foreground-700 hover:text-foreground-950'
              }`}>
                FAQ
              </a>
            </>
          ) : null}
          <Link
            to="/article/pandabuy-spreadsheet-guide"
            className={`text-sm font-medium whitespace-nowrap cursor-pointer ${
              isTransparent ? 'text-white/90 hover:text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)]' : 'text-foreground-700 hover:text-foreground-950'
            }`}
          >
            Shopping Guide
          </Link>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-2 md:gap-3">
          {/* Category Dropdown - desktop */}
          <div className="relative hidden md:block" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                isTransparent
                  ? 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]'
                  : 'bg-background-100 text-foreground-800 hover:bg-background-200'
              }`}
            >
              <i className="ri-grid-line"></i>
              <span>Categories</span>
              <i className={`ri-arrow-down-s-line text-xs transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute right-0 mt-2 w-64 bg-background-50 rounded-lg shadow-lg border border-background-200/70 py-2 max-h-[420px] overflow-y-auto">
                {categories.map((cat) => (
                  <button
                    key={cat.name}
                    onClick={() => handleCategorySelect(cat.url)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap text-left"
                  >
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className={`${cat.icon} text-foreground-400`}></i>
                    </span>
                    {cat.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Pandabuy Sheet CTA */}
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="flex items-center gap-1.5 px-4 md:px-5 py-2 md:py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 cursor-pointer whitespace-nowrap"
          >
            <span className="hidden sm:inline">Pandabuy Sheet</span>
            <span className="sm:hidden">Sheet</span>
            <i className="ri-arrow-right-up-line text-xs"></i>
          </a>

          {/* Mobile menu toggle */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer ${
              isTransparent
                ? 'text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)]'
                : 'text-foreground-800'
            }`}
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60 max-h-[70vh] overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            <Link
              to="/"
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-home-line"></i>
              </span>
              Home
            </Link>
            <Link
              to="/article/pandabuy-spreadsheet-guide"
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-book-open-line"></i>
              </span>
              Shopping Guide
            </Link>

            {/* Mobile Categories */}
            <div className="pt-2 pb-1">
              <p className="px-3 py-1 text-xs font-semibold text-foreground-400 uppercase tracking-wider">
                Categories
              </p>
            </div>
            {categories.map((cat) => (
              <button
                key={cat.name}
                onClick={() => handleCategorySelect(cat.url)}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm text-foreground-700 hover:bg-background-100 cursor-pointer text-left"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className={`${cat.icon} text-foreground-400`}></i>
                </span>
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}